import { styled } from "styled-components";

import { Cog8ToothIcon } from "@vericus/cadmus-icons";
import {
  Colored,
  colors,
  LinkButton,
  SectionLabel,
  Spacer,
  Text,
} from "@vericus/cadmus-ui";

import emptyImg from "@/assets/edit/requirements-empty.svg";
import {
  AssessmentType,
  RequirementsFragment,
  TaskFormat,
  TaskKind,
} from "@/generated/graphql";

import {
  AssessmentNoun,
  selectAssessmentNounForTask,
} from "../../assessment-utils";
import { IReqsDifference } from "../diffRequirements";
import {
  HiddenAssignmentRequirements,
  VisibleAssignmentRequirements,
} from "./assignment-requirements";
import {
  HiddenExamRequirements,
  RequirementViewErrors,
  VisibleExamRequirements,
} from "./exam-requirements";

interface RequirementViewProps {
  /** Requirements to display */
  requirements: RequirementsFragment;
  /** Which requirements has changes */
  diff?: IReqsDifference;
  /** Optional Callback called when editing the current Requirements */
  onEdit?: () => void;
  /** Optionally show the hidden settings.  */
  showHidden?: boolean;
  /** Show the legend for comparison against previously released requirements */
  showLegend?: boolean;
  /** Whether the empty state should be rendered by the component. */
  shouldShowEmptyState?: boolean;
  examErrors?: RequirementViewErrors;
  /** Format of the task */
  format?: TaskFormat;
  /** Kind of the task, used to adjust assessment requirements labels */
  kind?: TaskKind;
  /** Optional Callback called for exam start date. */
  setExamStart?: VoidFunction;
  /** Optional Callback called for set final due date. */
  setFinalDueDate?: VoidFunction;
}

/**
 * Display user-friendly readonly information on provided Requirements.
 *
 * Requirements can be split into 'public' and 'hidden'. Public requirements are
 * everything a student can see. Hidden requirements are only for lecturer
 * eyes. The display of the hidden requirements is controlled using the
 * `showHidden` prop.
 *
 * When requirements are empty, a nice illustration directing the user to update
 * them can be shown using the `showEmpty` prop. Otherwise, the default
 * behaviour is to show an empty section.
 */
export function RequirementView({
  requirements,
  diff,
  onEdit,
  showHidden,
  showLegend,
  examErrors,
  shouldShowEmptyState,
  format,
  kind = TaskKind.Individual,
  setExamStart,
  setFinalDueDate,
}: RequirementViewProps) {
  const isExam = requirements.assessmentType === AssessmentType.Exam;
  const assessmentNoun = selectAssessmentNounForTask({ kind, requirements });

  if (shouldShowEmptyState === true) {
    return (
      <>
        <SectionLabel title="Requirements" />
        <EmptyRequirements assessmentNoun={assessmentNoun} />
        {onEdit && (
          <>
            <Spacer spacing={27} />
            <LinkButton
              kind="primary"
              icon={<Cog8ToothIcon size={24} label="" />}
              onClick={onEdit}
            >
              Set requirements, grading options and settings
            </LinkButton>
          </>
        )}
      </>
    );
  }

  return (
    <>
      <StyledSectionLabel
        title={assessmentNoun === "Exam" ? "Exam details" : "Requirements"}
      />
      <Spacer spacing={27} />
      <>
        {isExam ? (
          <VisibleExamRequirements
            reqs={requirements}
            diff={diff}
            goToEditSettings={onEdit}
            validationErrors={examErrors}
            format={format}
            kind={kind}
            setExamStart={setExamStart}
            assessmentNoun={assessmentNoun}
          />
        ) : (
          <VisibleAssignmentRequirements
            reqs={requirements}
            diff={diff}
            setFinalDueDate={setFinalDueDate}
            format={format}
          />
        )}

        {showHidden === true &&
          (isExam ? (
            <HiddenExamRequirements
              reqs={requirements}
              diff={diff}
              validationErrors={examErrors}
              goToEditSettings={onEdit}
              format={format}
              kind={kind}
              assessmentNoun={assessmentNoun}
            />
          ) : (
            <HiddenAssignmentRequirements
              reqs={requirements}
              diff={diff}
              format={format}
            />
          ))}
      </>
      {diff && showLegend === true && (
        <>
          <Spacer spacing={27} />
          <Text kind="bodyMd">
            <Colored color="red500">*</Colored> <em>unreleased changes</em>
          </Text>
        </>
      )}
      {onEdit && (
        <>
          <Spacer spacing={27} />
          <LinkButton
            kind="primary"
            icon={<Cog8ToothIcon size={24} label="" />}
            onClick={onEdit}
          >
            Set requirements, grading options and settings
          </LinkButton>
        </>
      )}
    </>
  );
}

const EmptyRequirements = ({
  assessmentNoun,
}: {
  assessmentNoun: AssessmentNoun;
}) => (
  <EmptyContainer>
    <ImgContainer>
      <Img
        role="presentation"
        src={emptyImg}
        alt="A doodle illustration of a gear."
      />
    </ImgContainer>
    <div>
      <Text kind="headingFive">
        Set {assessmentNoun.toLowerCase()} requirements and grade settings
      </Text>
      <Text kind="bodyLine" color="shade1">
        {assessmentNoun === "Assignment" ? (
          <>
            Set the details of the assignment, like due dates and referencing
            styles.
            <br />
            Students will see these requirements in Cadmus.
          </>
        ) : (
          <>
            Set details of this {assessmentNoun.toLowerCase()}, like duration
            and enable auto submission.
            <br />
            Students will see these requirements in Cadmus.
          </>
        )}
      </Text>
    </div>
  </EmptyContainer>
);

const EmptyContainer = styled.div`
  width: 100%;
  margin-top: 30px;
  display: flex;
  align-items: center;
  gap: 24px;
`;

const ImgContainer = styled.div`
  display: flex;
  justify-content: center;
`;

const Img = styled.img`
  display: block;
  height: 63px;
`;

const StyledSectionLabel = styled(SectionLabel)`
  p {
    color: ${colors.grey600};
  }
`;
